import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { ActionData, ActionsOperators, Entities } from '../../data-rules-model';
import {
  ENRICH_CREDIT_ICON_FULL,
  ENRICH_CREDIT_EXHAUSTED_TOOLTIP,
  EnrichmentCreditService,
  enrichmentCreditIcon,
  enrichmentCreditPillTooltip,
  enrichmentCreditsRemaining,
  IEnrichmentCreditBalance
} from '../../../enrichment-settings/enrichment-credit.service';
import { EnrichmentFieldMappingsService } from '../../../enrichment-settings/field-mappings/enrichment-field-mappings.service';
import { EnrichmentSourceFieldsService } from '../../../enrichment-settings/source-fields/enrichment-source-fields.service';
import { CompactNumberValuesPipe } from '../../../kyloe-pipes.pipe';
import { SkeletonComponent } from '../../../shared/skeleton/skeleton.component';

@Component({
  selector: 'app-action-trust-score',
  imports: [ReactiveFormsModule, MatButtonToggleGroup, MatButtonToggle, RouterLink, MatTooltip, CompactNumberValuesPipe, SkeletonComponent],
  templateUrl: './enrich-action.component.html',
  styleUrls: ['./enrich-action.component.scss']
})
export class EnrichActionComponent implements OnInit, OnDestroy {
  @Input() actionData: ActionData;
  @Input() actionForm: FormGroup;
  @Input() primaryEntity: Entities;

  actionTypeControl = new FormControl(null);
  sourceFieldsControl = new FormControl('');
  liteControl = new FormControl(false);
  mappingStatusControl = new FormControl(null, Validators.required);
  creditStatusControl = new FormControl('ok', Validators.required);

  selectedSourceFields: string[] = [];
  enrichmentMappingsConfigured: boolean | null = null;
  creditsExhausted = false;
  readonly creditsExhaustedMessage = 'Full ' + ENRICH_CREDIT_EXHAUSTED_TOOLTIP + '.';

  creditBalance: IEnrichmentCreditBalance | null = null;
  creditsRemaining = 0;
  creditIcon = ENRICH_CREDIT_ICON_FULL;
  creditTooltip = '';

  private destroyRef = inject(DestroyRef);

  constructor(
    private enrichmentSourceFieldsService: EnrichmentSourceFieldsService,
    private enrichmentFieldMappingsService: EnrichmentFieldMappingsService,
    private enrichmentCreditService: EnrichmentCreditService
  ) {}

  ngOnInit() {
    this.createControls();
    this.checkEnrichmentMappings();
    this.checkEnrichmentCredit();
  }

  ngOnDestroy() {
    this.actionForm.removeControl('type');
    this.actionForm.removeControl('source');
    this.actionForm.removeControl('lite');
    this.actionForm.removeControl('mappingStatus');
    this.actionForm.removeControl('creditStatus');
  }

  private checkEnrichmentCredit(): void {
    this.enrichmentCreditService.getCreditBalance().subscribe({
      next: balance => {
        this.creditBalance = balance;
        this.creditsRemaining = enrichmentCreditsRemaining(balance);
        this.creditsExhausted = this.creditsRemaining <= 0;
        this.creditIcon = enrichmentCreditIcon(this.creditsRemaining);
        this.creditTooltip = enrichmentCreditPillTooltip(this.creditsRemaining);
        this.updateCreditStatus();
      }
    });
    this.liteControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateCreditStatus());
  }

  private updateCreditStatus(): void {
    const blocked = !this.liteControl.value && this.creditsExhausted;
    this.creditStatusControl.setValue(blocked ? null : 'ok');
  }

  private checkEnrichmentMappings(): void {
    this.enrichmentFieldMappingsService.getFieldMappings().subscribe(config => {
      this.enrichmentMappingsConfigured = !!(config?.mappings?.['candidateiq.trust_score']);
      this.mappingStatusControl.setValue(this.enrichmentMappingsConfigured ? 'configured' : null);
    });
  }

  private updateSourceControl(): void {
    this.sourceFieldsControl.markAsDirty();
    this.sourceFieldsControl.setValue(this.selectedSourceFields.join(','));
  }

  private createControls(): void {
    this.actionTypeControl = new FormControl(this.actionData.type);
    const rawSource = this.actionData.source || '';
    this.selectedSourceFields = rawSource ? rawSource.split(',').map(f => f.trim()).filter(Boolean) : [];
    this.sourceFieldsControl = new FormControl(rawSource);
    this.liteControl = new FormControl(this.actionData.lite ?? false);
    this.mappingStatusControl = new FormControl(null, Validators.required);

    this.actionForm.addControl('type', this.actionTypeControl);
    this.actionForm.addControl('source', this.sourceFieldsControl);
    this.actionForm.addControl('lite', this.liteControl);
    this.actionForm.addControl('mappingStatus', this.mappingStatusControl);
    this.actionForm.addControl('creditStatus', this.creditStatusControl);
    this.actionTypeControl.setValue(ActionsOperators.ENRICH);

    if (!rawSource) {
      this.enrichmentSourceFieldsService.getSourceFields().subscribe(fields => {
        this.selectedSourceFields = fields;
        this.updateSourceControl();
      });
    }
  }
}
